import { useEffect, useMemo, useState } from "react";
import { InputAdornment, useTheme } from "@mui/material";
import {
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MaterialReactTable,
  useMaterialReactTable
} from "material-react-table";
import { useTranslation } from "react-i18next";
import { ServiceTableArrangementRow } from "./typings/ServiceRoomLayout.types";
import { IZoneSchedule } from "../../../../../Domain/Types/FloorPlan/ZoneSchedule";
import { commonLocalization } from "../../../../../../../functions/tableLocalization";
import { useRemoteAssignRoomlayoutService } from "../../../../../../../hooks/Remote/FloorPlan/useRemoteAssignRoomlayoutService";
import {
  handleInitialRoomLayoutSelect,
  renderRoomLayoutCell,
  renderRoomLayoutSimpleText
} from "./RoomLayoutTable.partial";
import {
  AssignedRoomlayout,
  RemoteRoomlayout,
  RoomlayoutIds
} from "../../../../../Domain/Types/FloorPlan/RoomlayoutAssignment.type";
import {
  renderServiceBookingLeadTime,
  renderServiceImage,
  renderServiceSetupTime
} from "../ServicePartial/ServiceCommonRender.partial";
import { ServiceTableBottomToolbar } from "../ServicePartial/ServiceTableBottomToolbar.component";
import { ServiceValidationWithId } from "../ServicePartial/typings/ServiceValidationWithId.type";
import { validateAndSetErrors } from "../ServicePartial/ServiceTimeSetValidation.funtion";
import {
  handleChangeBookingLeadTime,
  handleChangeSetUpTime
} from "../ServicePartial/ServiceHandleTimeChange.function";
import { EnlargedImageDialog } from "../../../../../../../features/EnlargedImageCarousel/enlarged-image-dialog.component";
import * as _ from "lodash";
import { withSeeditMrtInitialState } from "../../../../../../../themes/mrt-table-theme";
import { useSeeditMrtTableOptions } from "../../../../../../../themes/use-seedit-mrt-table-options";

type Props = {
  zoneSchedule: IZoneSchedule;
  roomLayout: RemoteRoomlayout[];
  assignedRoomLayout: AssignedRoomlayout[];
  onChangeConfService: (id: number, service: { roomlayouts: RoomlayoutIds[] }) => void;
  onClose: () => void;
};

/**
 * A tables that displays bookable equipments
 */
export default function ServiceRoomLayoutTable({
  zoneSchedule,
  roomLayout,
  assignedRoomLayout,
  onChangeConfService,
  onClose
}: Readonly<Props>) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const [data, setData] = useState<ServiceTableArrangementRow[]>();
  const [validationErrors, setValidationErrors] = useState<ServiceValidationWithId>({});
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const [showBiggerImage, setShowBiggerImage] = useState<string | null>(null);
  const [isMandatory, setIsMandatory] = useState(false);

  const { mutateAsync: assignRoomLayout, isLoading: mutateServiceIsLoading } =
    useRemoteAssignRoomlayoutService();

  const handleAssign = () => {
    const value = table
      .getSelectedRowModel()
      .rows.map(({ original: { id, bookingLeadTime, setUpTime } }) => ({
        setUpTime: setUpTime,
        bookingLeadTime: bookingLeadTime,
        roomLayoutId: id,
        selectingIsMandatory: isMandatory
      }));

    const finishAssign = () => {
      assignRoomLayout({ zoneScheduleId: zoneSchedule.id, roomLayouts: value }).then(() => {
        onChangeConfService(zoneSchedule.id, { roomlayouts: value });
        onClose();
      });
    };

    validateAndSetErrors(
      value.map(val => ({ id: val.roomLayoutId, ...val })),
      setValidationErrors,
      finishAssign
    );
  };

  const column = useMemo<MRT_ColumnDef<ServiceTableArrangementRow>[]>(
    () => [
      {
        accessorKey: "layout",
        header: t("Layout"),
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => renderRoomLayoutCell(cell.row.original.layout, theme),
        size: 160,
        enableEditing: false
      },
      {
        accessorKey: "picturePath",
        header: t("Image"),
        enableEditing: false,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) =>
          renderServiceImage(
            100,
            cell.row.original.picturePaths
              ? cell.row.original.picturePaths[cell.row.original.picturePaths.length - 1]
              : null,
            setShowBiggerImage
          ),
        size: 100
      },
      {
        accessorKey: "name",
        header: t("Name"),
        size: 200,
        enableEditing: false,
        Cell: ({ cell }) => renderRoomLayoutSimpleText(cell.row.original.name)
      },
      {
        accessorKey: "description",
        header: t("Description"),
        size: 200,
        enableEditing: false,
        Cell: ({ cell }) => renderRoomLayoutSimpleText(cell.row.original.description)
      },
      {
        header: t("Booking Lead Time"),
        accessorKey: "bookingLeadTime",
        size: 150,
        enableEditing: true,
        muiTableBodyCellProps: {
          sx: { textAlign: "end" }
        },
        Header: renderServiceBookingLeadTime(),
        muiEditTextFieldProps: ({ row }) => {
          return {
            onChange: val => {
              handleChangeBookingLeadTime({
                val,
                rowId: row.original.id,
                validationErrors,
                setValidationErrors
              });
              row.original.bookingLeadTime = Number(val.target.value);
            },
            error: !!validationErrors[row.original.id]?.bookingLeadTime,
            helperText: validationErrors[row.original.id]?.bookingLeadTime,
            type: "number",
            inputProps: { step: 1, min: 0.0 },
            InputProps: {
              endAdornment: (
                <InputAdornment sx={{ pl: 2 }} position="start">
                  {row.original.bookingLeadTime && row.original.bookingLeadTime > 1
                    ? t("Hours")
                    : t("Hour")}
                </InputAdornment>
              )
            },
            sx: { width: "150px" }
          };
        }
      },
      {
        header: t("Setup Time"),
        accessorKey: "setUpTime",
        size: 150,
        enableEditing: true,
        muiTableBodyCellProps: {
          sx: { textAlign: "end" }
        },
        Header: renderServiceSetupTime(),
        muiEditTextFieldProps: ({ row }) => {
          return {
            helperText: validationErrors[row.original.id]?.setUpTime,
            type: "number",
            error: !!validationErrors[row.original.id]?.setUpTime,
            inputProps: { step: 5, min: 0 },
            InputProps: {
              endAdornment: (
                <InputAdornment sx={{ pl: 2 }} position="start">
                  {t("mins")}
                </InputAdornment>
              )
            },
            onChange: val => {
              handleChangeSetUpTime({
                val,
                rowId: row.original.id,
                validationErrors,
                setValidationErrors
              });
              row.original.setUpTime = Number(val.target.value);
            },
            sx: { width: "150px" }
          };
        }
      }
    ],
    [i18n.language, validationErrors]
  );

  useEffect(() => {
    if (!roomLayout) return;

    const arrData = _.uniqBy(
      [...assignedRoomLayout, ...roomLayout],
      layout => layout.id
    ) as AssignedRoomlayout[];

    const initialMandatory = arrData.some(item => item.selectingIsMandatory === true);
    const modified = arrData.map(el => ({
      ...el,
      layout: el.arrangement,
      name: el.name,
      description: el.description,
      picturePaths: el.picturePaths,
      roomlayoutId: el.roomLayoutId,
      bookingLeadTime: el.bookingLeadTime ?? 1.0, // hour unit
      setUpTime: el.setUpTime ?? 15, // minute unit
      selectingIsMandatory: el.selectingIsMandatory ?? false
    }));

    setData(modified);
    setIsMandatory(initialMandatory);

    if (assignedRoomLayout.length)
      handleInitialRoomLayoutSelect(assignedRoomLayout, setRowSelection);
  }, [zoneSchedule, roomLayout, assignedRoomLayout]);
  const seeditMrtOptions = useSeeditMrtTableOptions<ServiceTableArrangementRow>();

  const table = useMaterialReactTable({
    ...seeditMrtOptions,
    columns: column ?? [],
    data: data ?? [],
    getRowId: row => row.id?.toString(),
    state: { isSaving: mutateServiceIsLoading, rowSelection },
    initialState: withSeeditMrtInitialState({
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 5, pageIndex: 0 }
    }),
    editDisplayMode: "table",
    paginationDisplayMode: "pages",
    onRowSelectionChange: setRowSelection,
    localization: {
      ...commonLocalization(t),
      noRecordsToDisplay: t("_editable_list_noRecords", { item: t("_noRoomlayout") })
    },
    autoResetPageIndex: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: true,
    enableEditing: true,
    enableMultiRowSelection: true,
    enableRowSelection: true,
    positionToolbarAlertBanner: "none",
    renderBottomToolbar: ({ table }) => (
      <ServiceTableBottomToolbar
        table={table as any}
        isMandatory={isMandatory}
        isDisabled={!table.getSelectedRowModel().rows.length}
        setIsMandatory={setIsMandatory}
        handleAssign={handleAssign}
        onClose={onClose}
      />
    ),
    muiPaginationProps: { rowsPerPageOptions: [5, 10, 15], variant: "outlined" },
    muiTableContainerProps: {
      sx: { maxHeight: "calc(80vh - 250px)" }
    }
  });

  return (
    <>
      <MaterialReactTable table={table} />

      <EnlargedImageDialog
        open={!!showBiggerImage}
        picturePath={showBiggerImage}
        handleClose={() => setShowBiggerImage(null)}
      />
    </>
  );
}
